/**
 * AbsenceDashboard.jsx — Absence dashboard (Unexcused / Excused)
 * King's Academy Faculty Tool Kit
 *
 * Reads /absenceSummaries/{seasonId} for Upper School and
 * /absenceSummaries/{seasonId}__ms for Middle School. Toggles between
 * Unexcused, Excused, and Both views over the same loaded data (no refetch),
 * and between the Upper and Middle divisions (each reload fetches that
 * division's summary + events). Read-only: Veracross is the source of truth, so
 * there is no manual-entry or edit path here. Drills into the events
 * subcollection per student. Recompute is admin-triggered, per division.
 *
 * Import paths are set for this tree: db from ../config/firebase, useAuth from
 * ../contexts/AuthContext.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "../config/firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";

const KA_RED = "#E83530";
const KA_NAVY = "#233C5F";
const KA_GOLD = "#A29164";
const INK = "#1f2a37";
const MUTE = "#6b7280";
const LINE = "#e5e7eb";
const BG = "#f7f8fa";

const SEASONS_DOC = "config/mopSeasons";
const CONFIG_DOC = "config/absences";

const VIEWS = [
  { key: "unexcused", label: "Unexcused" },
  { key: "excused", label: "Excused" },
  { key: "both", label: "Both" },
];

const DIVISIONS = [
  { key: "upper", label: "Upper School" },
  { key: "middle", label: "Middle School" },
];

// Upper writes /absenceSummaries/{seasonId}; Middle writes {seasonId}__ms.
const summaryIdFor = (sid, division) =>
  !sid ? "" : division === "middle" ? `${sid}__ms` : sid;

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.map((r) => r.map(csvCell).join(",")).join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function bandFor(total, thresholds) {
  let hit = null;
  for (const t of thresholds) if (total >= t.points) hit = t;
  return hit;
}

const countFor = (r, view) => (view === "excused" ? r.excusedCount || 0 : r.unexcusedCount || 0);

export default function AbsenceDashboard() {
  const auth = useAuth() || {};
  const isSuperAdmin = !!auth.isSuperAdmin;
  const role = String(
    auth.role || (auth.user && auth.user.role) || (auth.profile && auth.profile.role) || ""
  ).toLowerCase();
  const canRecalc = isSuperAdmin || ["dean", "admin", "superadmin"].includes(role);

  const fns = useMemo(() => getFunctions(getApp(), "us-central1"), []);

  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [thresholds, setThresholds] = useState([]);

  const [view, setView] = useState("unexcused");
  const [division, setDivision] = useState("upper");

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [advisorFilter, setAdvisorFilter] = useState("");
  const [minCount, setMinCount] = useState(0);
  const [sortKey, setSortKey] = useState("unexcusedCount");
  const [sortDir, setSortDir] = useState("desc");

  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const [showUnresolved, setShowUnresolved] = useState(false);

  const divisionLabel = division === "middle" ? "Middle School" : "Upper School";

  useEffect(() => {
    (async () => {
      try {
        const [seasonSnap, cfgSnap] = await Promise.all([
          getDoc(doc(db, SEASONS_DOC)),
          getDoc(doc(db, CONFIG_DOC)),
        ]);
        const seasonArr =
          seasonSnap.exists() && Array.isArray(seasonSnap.data().seasons)
            ? seasonSnap.data().seasons
            : [];
        setSeasons(seasonArr);

        const cfg = cfgSnap.exists() ? cfgSnap.data() : {};
        const th = Array.isArray(cfg.thresholds) ? [...cfg.thresholds] : [];
        th.sort((a, b) => (a.points || 0) - (b.points || 0));
        setThresholds(th);

        const today = new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
        const active =
          seasonArr.find(
            (s) => s.startDate && today >= s.startDate && (!s.endDate || today <= s.endDate)
          ) || seasonArr.find((s) => s.startDate) || seasonArr[0];
        setSeasonId((active && active.id) || "");
      } catch (e) {
        setError("Could not load season configuration.");
      }
    })();
  }, []);

  const loadSummary = useCallback(async (sid, div) => {
    const summaryId = summaryIdFor(sid, div);
    if (!summaryId) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const snap = await getDoc(doc(db, "absenceSummaries", summaryId));
      setSummary(snap.exists() ? snap.data() : null);
    } catch (e) {
      setError("Could not load the absence summary. Check Firestore rules for /absenceSummaries.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload whenever the season OR the division changes.
  useEffect(() => {
    setSelected(null);
    setEvents([]);
    loadSummary(seasonId, division);
  }, [seasonId, division, loadSummary]);

  useEffect(() => {
    setSortKey(view === "excused" ? "excusedCount" : "unexcusedCount");
    setSortDir("desc");
    setSelected(null);
    setEvents([]);
  }, [view]);

  const rows = useMemo(() => {
    const map = (summary && summary.students) || {};
    let list = Object.values(map);

    if (view !== "both") list = list.filter((r) => countFor(r, view) > 0);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          (r.person || "").toLowerCase().includes(q) || String(r.personId || "").includes(q)
      );
    }
    if (gradeFilter) list = list.filter((r) => String(r.grade ?? "") === gradeFilter);
    if (advisorFilter) list = list.filter((r) => (r.advisorName || "") === advisorFilter);
    if (minCount > 0) {
      const metric = view === "excused" ? "excusedCount" : "unexcusedCount";
      list = list.filter((r) => (r[metric] || 0) >= minCount);
    }

    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === "person" || sortKey === "advisorName") {
        av = (av || "").toLowerCase();
        bv = (bv || "").toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      }
      return (Number(av || 0) - Number(bv || 0)) * dir;
    });
    return list;
  }, [summary, view, search, gradeFilter, advisorFilter, minCount, sortKey, sortDir]);

  const grades = useMemo(() => {
    const set = new Set();
    Object.values((summary && summary.students) || {}).forEach((r) => {
      if (r.grade != null && r.grade !== "") set.add(String(r.grade));
    });
    return [...set].sort();
  }, [summary]);

  const advisors = useMemo(() => {
    const set = new Set();
    Object.values((summary && summary.students) || {}).forEach((r) => {
      if (r.advisorName) set.add(r.advisorName);
    });
    return [...set].sort();
  }, [summary]);

  const openStudent = useCallback(
    async (personId) => {
      if (selected === personId) {
        setSelected(null);
        setEvents([]);
        return;
      }
      setSelected(personId);
      setEvents([]);
      setEventsLoading(true);
      try {
        const summaryId = summaryIdFor(seasonId, division);
        const qy = query(
          collection(db, "absenceSummaries", summaryId, "events"),
          where("personId", "==", String(personId))
        );
        const snap = await getDocs(qy);
        const list = [];
        snap.forEach((d) => list.push({ _id: d.id, ...d.data() }));
        list.sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
        setEvents(list);
      } catch (e) {
        setError("Could not load this student's absence events.");
      } finally {
        setEventsLoading(false);
      }
    },
    [selected, seasonId, division]
  );

  async function handleRecalc() {
    if (!seasonId) return;
    setRecalcBusy(true);
    setError("");
    setNotice("");
    try {
      const call = httpsCallable(fns, "rebuildAbsenceSummary");
      const res = await call({ seasonId, division });
      const d = res.data || {};
      const divLabel = d.division === "middle" ? "Middle School" : "Upper School";
      let msg =
        `Recomputed ${divLabel} ${d.seasonId}: ${d.totalUnexcused} unexcused / ${d.totalExcused} excused ` +
        `across ${d.studentCount} students, ${d.datesScanned} days.`;
      if ((d.totalUnexcused || 0) === 0 && (d.totalExcused || 0) === 0) {
        const rawTotal = Object.values(d.rawByStatus || {}).reduce((a, b) => a + b, 0);
        if (rawTotal === 0) {
          msg += "  ⚠ No records matched the configured statuses — check absenceStatuses/excusedStatuses.";
        } else if (d.division === "middle") {
          msg += `  ⚠ ${rawTotal} records matched but none landed in Middle School — ${d.scopedOut} had no resolved student and ${d.gradeOut} were not in Grade 7/8.`;
        } else {
          msg += `  ⚠ ${rawTotal} records matched but ${d.scopedOut} were scoped out — check divisionField/middleSchoolValues.`;
        }
      }
      setNotice(msg);
      await loadSummary(seasonId, division);
      if (selected) {
        const pid = selected;
        setSelected(null);
        setTimeout(() => openStudent(pid), 0);
      }
    } catch (e) {
      setError(e.message || "Recalculate failed.");
    } finally {
      setRecalcBusy(false);
    }
  }

  function exportCsv() {
    const header = ["Name", "ID", "Grade", "Advisor", "Unexcused", "Excused", "Last unexcused"];
    const body = rows.map((r) => [
      r.person || "",
      r.personId || "",
      r.grade ?? "",
      r.advisorName || "",
      r.unexcusedCount || 0,
      r.excusedCount || 0,
      r.lastDate || "",
    ]);
    downloadCsv(`absences-${division}-${view}-${seasonId || "season"}.csv`, [header, ...body]);
  }

  const stats = useMemo(() => {
    const totalStudents = rows.length;
    const totalUnexcused = rows.reduce((s, r) => s + (r.unexcusedCount || 0), 0);
    const totalExcused = rows.reduce((s, r) => s + (r.excusedCount || 0), 0);
    const flagged =
      thresholds.length && view !== "excused"
        ? rows.filter((r) => bandFor(r.unexcusedCount || 0, thresholds)).length
        : 0;
    return { totalStudents, totalUnexcused, totalExcused, flagged };
  }, [rows, thresholds, view]);

  const unresolved = (summary && summary.unresolvedStudentIds) || [];
  const showThresholdCol = thresholds.length > 0 && view !== "excused";
  const showUnexcusedCol = view === "unexcused" || view === "both";
  const showExcusedCol = view === "excused" || view === "both";
  const colCount =
    4 + (showUnexcusedCol ? 1 : 0) + (showExcusedCol ? 1 : 0) + (showThresholdCol ? 1 : 0);

  return (
    <div style={{ padding: "24px 28px", background: BG, minHeight: "100vh", color: INK }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ margin: 0, fontSize: 24, color: KA_NAVY }}>Absences</h1>
          <div style={{ color: MUTE, fontSize: 13, marginTop: 4 }}>
            King&rsquo;s Academy &middot; Madaba, Jordan &middot; {divisionLabel}
            {summary && summary.dedupePerDay === false ? " · per-class count" : " · per-day count"}
          </div>
        </div>

        <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)} style={selStyle}>
          {seasons.length === 0 && <option value="">No seasons configured</option>}
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label || s.id}
            </option>
          ))}
        </select>

        {canRecalc && (
          <button onClick={handleRecalc} disabled={recalcBusy || !seasonId} style={btn(KA_NAVY)}>
            {recalcBusy ? "Recalculating…" : "Recalculate"}
          </button>
        )}
      </div>

      {/* Division toggle: Upper / Middle School */}
      <div style={{ display: "flex", gap: 0, marginTop: 16, border: `1px solid ${LINE}`, borderRadius: 10, overflow: "hidden", width: "fit-content" }}>
        {DIVISIONS.map((d) => (
          <button
            key={d.key}
            onClick={() => setDivision(d.key)}
            style={{
              padding: "8px 18px",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              background: division === d.key ? KA_GOLD : "#fff",
              color: division === d.key ? "#fff" : MUTE,
            }}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* View toggle: Unexcused / Excused / Both */}
      <div style={{ display: "flex", gap: 0, marginTop: 12, border: `1px solid ${LINE}`, borderRadius: 10, overflow: "hidden", width: "fit-content" }}>
        {VIEWS.map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            style={{
              padding: "8px 18px",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              background: view === v.key ? KA_NAVY : "#fff",
              color: view === v.key ? "#fff" : MUTE,
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {error && <Banner color={KA_RED}>{error}</Banner>}
      {notice && <Banner color={KA_NAVY}>{notice}</Banner>}

      <div style={{ display: "flex", gap: 12, margin: "18px 0", flexWrap: "wrap" }}>
        <Stat label="Students" value={stats.totalStudents} color={KA_NAVY} />
        {showUnexcusedCol && <Stat label="Total unexcused" value={stats.totalUnexcused} color={KA_RED} />}
        {showExcusedCol && <Stat label="Total excused" value={stats.totalExcused} color={MUTE} />}
        {showThresholdCol && <Stat label="At/over a threshold" value={stats.flagged} color={KA_GOLD} />}
        {summary && (
          <Stat
            label="Days scanned"
            value={summary.datesScanned || 0}
            color={MUTE}
            sub={summary.rebuiltAt ? `rebuilt ${summary.rebuiltAt.slice(0, 16).replace("T", " ")}` : ""}
          />
        )}
      </div>

      {loading ? (
        <Card>Loading…</Card>
      ) : !summary ? (
        <Card>
          <p style={{ marginTop: 0 }}>
            No {divisionLabel} summary exists yet for <strong>{seasonId || "this season"}</strong>.
          </p>
          {canRecalc ? (
            <button onClick={handleRecalc} disabled={recalcBusy || !seasonId} style={btn(KA_NAVY)}>
              {recalcBusy ? "Recalculating…" : "Recalculate now"}
            </button>
          ) : (
            <p style={{ color: MUTE }}>Ask an administrator to run the first recompute.</p>
          )}
        </Card>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <input
              placeholder="Search name or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, minWidth: 220 }}
            />
            <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} style={selStyle}>
              <option value="">All grades</option>
              {grades.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <select value={advisorFilter} onChange={(e) => setAdvisorFilter(e.target.value)} style={selStyle}>
              <option value="">All advisors</option>
              {advisors.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: MUTE, fontSize: 13 }}>
              Min
              <input
                type="number"
                min={0}
                value={minCount}
                onChange={(e) => setMinCount(Number(e.target.value) || 0)}
                style={{ ...inputStyle, width: 70 }}
              />
            </label>
            <div style={{ flex: 1 }} />
            <button onClick={exportCsv} style={btn(KA_GOLD)}>Export CSV</button>
          </div>

          <Card pad={0}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#fbfcfd", borderBottom: `2px solid ${LINE}` }}>
                  <Th onClick={() => toggleSort("person", sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === "person"} dir={sortDir}>Student</Th>
                  <Th>ID</Th>
                  <Th onClick={() => toggleSort("grade", sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === "grade"} dir={sortDir}>Grade</Th>
                  <Th onClick={() => toggleSort("advisorName", sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === "advisorName"} dir={sortDir}>Advisor</Th>
                  {showUnexcusedCol && (
                    <Th align="right" onClick={() => toggleSort("unexcusedCount", sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === "unexcusedCount"} dir={sortDir}>Unexcused</Th>
                  )}
                  {showExcusedCol && (
                    <Th align="right" onClick={() => toggleSort("excusedCount", sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === "excusedCount"} dir={sortDir}>Excused</Th>
                  )}
                  {showThresholdCol && <Th>Status</Th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={colCount} style={{ padding: 24, color: MUTE, textAlign: "center" }}>
                      No students match these filters.
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const band = bandFor(r.unexcusedCount || 0, thresholds);
                  const open = selected === r.personId;
                  return (
                    <React.Fragment key={r.personId}>
                      <tr
                        onClick={() => openStudent(r.personId)}
                        style={{ cursor: "pointer", borderBottom: `1px solid ${LINE}`, background: open ? "#eef2f7" : "transparent" }}
                      >
                        <td style={td}>{r.person || <span style={{ color: MUTE }}>Unknown</span>}</td>
                        <td style={{ ...td, color: MUTE }}>{r.personId}</td>
                        <td style={td}>{r.grade ?? ""}</td>
                        <td style={td}>{r.advisorName || ""}</td>
                        {showUnexcusedCol && (
                          <td style={{ ...td, textAlign: "right", fontWeight: 700, color: KA_NAVY }}>{r.unexcusedCount || 0}</td>
                        )}
                        {showExcusedCol && (
                          <td style={{ ...td, textAlign: "right", color: MUTE }}>{r.excusedCount || 0}</td>
                        )}
                        {showThresholdCol && (
                          <td style={td}>
                            {band ? <span style={pill(band.color || KA_GOLD)}>{band.label}</span> : <span style={{ color: MUTE }}>—</span>}
                          </td>
                        )}
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={colCount} style={{ background: "#f3f6fa", padding: 0 }}>
                            <DrillDown loading={eventsLoading} events={events} view={view} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {unresolved.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: "#9aa3af", textAlign: "right" }}>
              <span
                onClick={() => setShowUnresolved((v) => !v)}
                style={{ cursor: "pointer" }}
                title={`${divisionLabel} IDs with no /students match — counted, but unnamed`}
              >
                ⚑ {unresolved.length} unmatched ID{unresolved.length === 1 ? "" : "s"} {showUnresolved ? "▲" : "▼"}
              </span>
              {showUnresolved && (
                <div style={{ marginTop: 4, color: "#b6bcc6", fontFamily: "monospace" }}>
                  {unresolved.join(", ")}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function toggleSort(key, sortKey, sortDir, setSortKey, setSortDir) {
  if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
  else {
    setSortKey(key);
    setSortDir(key === "person" || key === "advisorName" ? "asc" : "desc");
  }
}

function DrillDown({ loading, events, view }) {
  const shown = view === "both" ? events : events.filter((e) => (e.kind || "unexcused") === view);
  if (loading) return <div style={{ padding: 16, color: MUTE }}>Loading events…</div>;
  if (!shown.length) return <div style={{ padding: 16, color: MUTE }}>No {view === "both" ? "" : view + " "}events.</div>;
  return (
    <div style={{ padding: "10px 16px" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ color: MUTE, textAlign: "left" }}>
            <th style={dth}>Date</th>
            <th style={dth}>Block(s)</th>
            <th style={dth}>Class</th>
            <th style={dth}>Teacher</th>
            <th style={dth}>Kind</th>
            <th style={{ ...dth, textAlign: "right" }}>Pts</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((e) => {
            const blocks =
              e.blocks && e.blocks.length
                ? e.blocks.join(", ") + (e.blockCount > e.blocks.length ? ` (${e.blockCount})` : "")
                : e.block || (e.blockCount ? `${e.blockCount} blocks` : "");
            const excused = (e.kind || "unexcused") === "excused";
            return (
              <tr key={e._id} style={{ borderTop: `1px solid ${LINE}` }}>
                <td style={dtd}>{e.date}</td>
                <td style={dtd}>{blocks}</td>
                <td style={dtd}>{e.class || ""}</td>
                <td style={dtd}>{e.teacherName || ""}</td>
                <td style={dtd}>
                  <span style={pill(excused ? MUTE : KA_RED)}>{excused ? "excused" : "unexcused"}</span>
                </td>
                <td style={{ ...dtd, textAlign: "right" }}>{e.points || 1}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ color: MUTE, fontSize: 11, marginTop: 6 }}>
        Read-only — Veracross is the source of truth. Correct any record there.
      </div>
    </div>
  );
}

function Banner({ color, children }) {
  return (
    <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: color + "14", border: `1px solid ${color}55`, color, fontSize: 13.5 }}>
      {children}
    </div>
  );
}

function Stat({ label, value, color, sub }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, padding: "12px 16px", minWidth: 150 }}>
      <div style={{ fontSize: 12, color: MUTE }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: MUTE }}>{sub}</div> : null}
    </div>
  );
}

function Card({ children, pad = 16 }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, padding: pad }}>
      {children}
    </div>
  );
}

function Th({ children, onClick, active, dir, align }) {
  return (
    <th
      onClick={onClick}
      style={{ textAlign: align || "left", padding: "10px 12px", fontSize: 12, color: MUTE, cursor: onClick ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap" }}
    >
      {children}
      {active ? (dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
}

const td = { padding: "9px 12px" };
const dth = { padding: "6px 8px", fontWeight: 600, fontSize: 12 };
const dtd = { padding: "6px 8px" };

const selStyle = { padding: "8px 10px", borderRadius: 8, border: `1px solid ${LINE}`, background: "#fff", fontSize: 14, color: INK };
const inputStyle = { padding: "8px 10px", borderRadius: 8, border: `1px solid ${LINE}`, fontSize: 14, color: INK };

function btn(bg) {
  return { padding: "8px 14px", borderRadius: 8, border: "none", background: bg, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" };
}
function pill(color) {
  return { display: "inline-block", padding: "2px 8px", borderRadius: 999, background: color + "22", color, fontSize: 12, fontWeight: 600 };
}